import ComposerAtMentionPanel from '@/components/ComposerAtMentionPanel.vue'
import { fetchAssetLibraryList } from '@/utils/assetLibraryApi'
import {
  buildAtMentionItems,
  buildAtMentionPanelStyle,
  detectAtMention
} from '@/utils/workflowComposerAtMention'
import { insertUploadInlineAtCursor } from '@/utils/workflowComposerInlineUpload'

export default {
  components: { ComposerAtMentionPanel },
  data() {
    return {
      atMentionOpen: false,
      atMentionQuery: '',
      atMentionIndex: 0,
      atMentionItems: [],
      atMentionPanelStyle: {},
      atMentionDelRange: null,
      atMentionLoading: false,
      _atMentionAssetRows: [],
      _atMentionAssetsLoaded: false,
      _atMentionDocMousedown: null
    }
  },
  watch: {
    atMentionOpen(v) {
      if (v) {
        this.$nextTick(() => this.portalAtMentionPanel())
      }
    }
  },
  beforeDestroy() {
    this.unbindAtMentionOutsideClick()
    this.closeAtMention()
  },
  methods: {
    portalAtMentionPanel() {
      const panel = this.$refs.atMentionPanel
      const el = panel && panel.$el
      if (!el || el.parentNode === document.body) return
      try {
        document.body.appendChild(el)
      } catch (e) {
        /* ignore */
      }
    },
    /** @returns {HTMLElement|null} */
    getAtMentionComposerEl() {
      const ref = this.atMentionComposerRef
      if (ref && this.$refs[ref]) return this.$refs[ref]
      return this.$refs.promptComposer || this.$refs.bottomComposer || null
    },

    getAtMentionLocalUploads() {
      return Array.isArray(this.localUploads) ? this.localUploads : []
    },

    getAtMentionAssetRows() {
      if (Array.isArray(this.assetRows) && this.assetRows.length) {
        return this.assetRows
      }
      return this._atMentionAssetRows || []
    },

    async ensureAtMentionAssets() {
      if (this._atMentionAssetsLoaded || this.atMentionLoading) return
      this.atMentionLoading = true
      try {
        const rows = await fetchAssetLibraryList({ limit: 200 })
        this._atMentionAssetRows = (rows || [])
          .map((x) => ({
            id: x.id != null ? String(x.id) : '',
            kind: x.kind || 'image',
            url: x.url || '',
            title: x.title || '未命名'
          }))
          .filter((r) => r.url)
        this._atMentionAssetsLoaded = true
      } catch (e) {
        this._atMentionAssetRows = []
      } finally {
        this.atMentionLoading = false
      }
    },

    bindAtMentionOutsideClick() {
      this.unbindAtMentionOutsideClick()
      this._atMentionDocMousedown = (e) => {
        if (!this.atMentionOpen) return
        const panel = this.$refs.atMentionPanel
        const el = panel && panel.$el ? panel.$el : null
        const composer = this.getAtMentionComposerEl()
        if (el && el.contains(e.target)) return
        if (composer && composer.contains(e.target)) return
        this.closeAtMention()
      }
      document.addEventListener('mousedown', this._atMentionDocMousedown, true)
    },

    unbindAtMentionOutsideClick() {
      if (!this._atMentionDocMousedown) return
      document.removeEventListener('mousedown', this._atMentionDocMousedown, true)
      this._atMentionDocMousedown = null
    },

    closeAtMention() {
      this.atMentionOpen = false
      this.atMentionQuery = ''
      this.atMentionIndex = 0
      this.atMentionItems = []
      this.atMentionPanelStyle = {}
      this.atMentionDelRange = null
      this.unbindAtMentionOutsideClick()
    },

    refreshAtMentionItems() {
      this.atMentionItems = buildAtMentionItems({
        localUploads: this.getAtMentionLocalUploads(),
        assetRows: this.getAtMentionAssetRows(),
        query: this.atMentionQuery
      })
      if (this.atMentionIndex >= this.atMentionItems.length) {
        this.atMentionIndex = Math.max(0, this.atMentionItems.length - 1)
      }
    },

    async syncAtMentionFromInput() {
      const composer = this.getAtMentionComposerEl()
      if (!composer) {
        this.closeAtMention()
        return
      }

      const state = detectAtMention(composer)
      if (!state) {
        if (this.atMentionOpen) this.closeAtMention()
        return
      }

      const wasOpen = this.atMentionOpen
      this.atMentionOpen = true
      this.atMentionQuery = state.query
      this.atMentionDelRange = state.delRange
      this.atMentionPanelStyle = buildAtMentionPanelStyle(state.delRange)

      if (!wasOpen) {
        await this.ensureAtMentionAssets()
        this.bindAtMentionOutsideClick()
      }
      this.refreshAtMentionItems()
    },

    isAtMentionKeydownHandled(e) {
      if (!this.atMentionOpen || !e) return false

      if (e.key === 'Escape') {
        e.preventDefault()
        this.closeAtMention()
        return true
      }

      const len = this.atMentionItems.length
      if (!len) return e.key === 'Escape'

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        this.atMentionIndex = (this.atMentionIndex + 1) % len
        return true
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        this.atMentionIndex = (this.atMentionIndex - 1 + len) % len
        return true
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        const item = this.atMentionItems[this.atMentionIndex]
        if (item) this.pickAtMentionItem(item)
        return true
      }
      return false
    },

    pickAtMentionItem(item) {
      if (!item || !item.url) return
      const composer = this.getAtMentionComposerEl()
      if (!composer) return

      const u = {
        kind: item.kind === 'video' ? 'video' : 'image',
        url: item.url,
        name: item.name || '未命名'
      }

      if (!Array.isArray(this.localUploads)) this.localUploads = []
      const exists = this.localUploads.some((x) => x && String(x.url) === String(u.url))
      if (!exists) this.localUploads.push(u)

      try {
        if (this.atMentionDelRange) {
          this.atMentionDelRange.deleteContents()
        }
      } catch (e) {
        /* ignore */
      }

      insertUploadInlineAtCursor(composer, u)
      this.closeAtMention()

      if (typeof this.onComposerInput === 'function') {
        this.$nextTick(() => this.onComposerInput())
      }
      if (typeof this.focusComposer === 'function') {
        this.focusComposer()
      } else if (composer.focus) {
        composer.focus()
      }
    }
  }
}
